// Ported from dev-scrips-pm-react src/billing/services/use-payments.ts (PROD-1119 cross-module closure).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, put } from '@/shared/api/api-client';
import { BILLING_BASE } from '@/shared/api/billing-client';
import { invalidateBalanceViews } from '@/billing/services/invalidate-balance-views';
import { showSuccess } from '@/shared/lib/toast-helpers';
import type {
  AddPaymentRequest,
  CancellationReasonRef,
  ClaimWorkbenchListResponse,
  PaymentRow,
  RefundPaymentRequest,
  VoidPaymentRequest,
} from '@/shared/types/billing';

/**
 * Payment void reasons for the Void dialog dropdown — Flutter
 * `getPaymentVoidReasons`. `GET /Billing/Payment/GetPaymentVoidReasons` →
 * `[{ id, value }]`. The chosen `{id,value}` is shipped verbatim as the
 * void `voidReason`. Rarely changes — cache for the session.
 */
export function useVoidReasons(enabled = true) {
  return useQuery({
    queryKey: ['payments', 'void-reasons'],
    queryFn: ({ signal }) =>
      get<CancellationReasonRef[]>(
        `${BILLING_BASE}/Payment/GetPaymentVoidReasons`,
        { signal },
      ),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

/**
 * Payment + Refund hooks (Receive / List / Void / Refund).
 *
 * Wire endpoints (Flutter `billing_data_source_impl.dart:1007-1250`):
 *
 *   GET  /Billing/Payment/PaymentReceipts?invoiceId={id}&showArchived=false
 *        → Payment[]
 *   GET  /Billing/Payment/Refunds/invoice/{invoiceId}
 *        → RefundPayment[]
 *   POST /Billing/Payment/Create/Invoice/{invoiceId}
 *        body: AddPaymentRequest                     — receive payment
 *   PUT  /Billing/Payment/Void/{receiptId}?linkedReceiptId={id}
 *        body: VoidPaymentRequest                    — void (note PUT not POST)
 *   POST /Billing/Payment/Refund/{receiptId}
 *        body: RefundPaymentRequest                  — refund
 *
 * On every successful mutation we invalidate:
 *   - The payments / refunds queries for the bound invoiceId
 *   - The bound invoice detail (so totals refresh)
 *   - The claim-workbench list root (so the row's payment status refreshes)
 */

const KEY_ROOT = ['payments'] as const;

export function usePaymentsForInvoice(invoiceId: string | undefined) {
  return useQuery({
    queryKey: [...KEY_ROOT, 'list', invoiceId],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams();
      qs.set('invoiceId', invoiceId ?? '');
      qs.set('showArchived', 'false');
      return get<PaymentRow[]>(
        `${BILLING_BASE}/Payment/PaymentReceipts?${qs.toString()}`,
        { signal },
      );
    },
    enabled: Boolean(invoiceId),
  });
}

export function useRefundsForInvoice(invoiceId: string | undefined) {
  return useQuery({
    queryKey: [...KEY_ROOT, 'refunds', invoiceId],
    queryFn: ({ signal }) =>
      get<PaymentRow[]>(
        `${BILLING_BASE}/Payment/Refunds/invoice/${invoiceId}`,
        { signal },
      ),
    enabled: Boolean(invoiceId),
  });
}

/**
 * Predicate that matches workbench list pages whose `results` array
 * contains a row bound to the given invoice. Exported for the unit
 * test; callers should go through `invalidatePaymentAndInvoice`.
 *
 * Workbench list cache key shape (from `use-claim-workbench.ts`):
 *
 *   `['claim-workbench', 'list', query, page, size, showArchived, JSON.stringify(filter)]`
 *
 * The invoice id is NOT in the key — it's in the cached response body —
 * so we predicate on the cached data, not the key.
 */
export function workbenchPageContainsInvoice(
  queryKey: readonly unknown[],
  data: unknown,
  invoiceId: string,
): boolean {
  if (
    !Array.isArray(queryKey) ||
    queryKey[0] !== 'claim-workbench' ||
    queryKey[1] !== 'list'
  ) {
    return false;
  }
  const typed = data as ClaimWorkbenchListResponse | undefined;
  if (!typed?.results?.length) return false;
  return typed.results.some((row) => row.invoice?.id === invoiceId);
}

/**
 * Invalidate the payment + invoice + claim-workbench query keys after a
 * successful mutation. Three layers refresh together — each narrowed to
 * only what the mutation actually affected (PROD-793, Samer review PR #111):
 *
 *  1. **Payments / refunds for the bound invoice** — exact-match keys, so
 *     OTHER invoices' lists never refetch.
 *  2. **The bound invoice's detail key** — exact-match.
 *  3. **Claim workbench pages containing this invoice's row** — predicate
 *     scans cached page data; only pages whose `results` actually
 *     contain a row with `invoice.id === invoiceId` get invalidated.
 *     Other filter / page / sort combinations stay cached.
 *
 * Workbench refetch cost on a payment event is now O(affected pages)
 * instead of O(cached pages). On the v1 dev dataset (1-2 cached pages
 * typical) the difference is negligible; the protection kicks in for
 * heavy operator sessions with many filter combos open.
 *
 * If no `invoiceId` is provided (defensive — shouldn't happen for our
 * dialogs), nothing is invalidated. The caller should always pass an id.
 */
function invalidatePaymentAndInvoice(
  qc: ReturnType<typeof useQueryClient>,
  invoiceId: string | undefined,
) {
  if (!invoiceId) return;
  // Layer 1 — payments + refunds, exact-match by invoiceId.
  void qc.invalidateQueries({ queryKey: [...KEY_ROOT, 'list', invoiceId] });
  void qc.invalidateQueries({ queryKey: [...KEY_ROOT, 'refunds', invoiceId] });
  // Layer 2 — bound invoice detail, exact-match.
  void qc.invalidateQueries({ queryKey: ['invoice', 'detail', invoiceId] });
  // Layer 3 — workbench pages that actually contain a row for this invoice.
  void qc.invalidateQueries({
    predicate: (q) =>
      workbenchPageContainsInvoice(q.queryKey, q.state.data, invoiceId),
  });
  // Layer 4 — balance/total read models not keyed by invoiceId (Total Unpaid /
  // Available Balance / patient invoices) so they don't go stale (PROD-1809).
  invalidateBalanceViews(qc);
}

export interface AddPaymentVars {
  invoiceId: string;
  request: AddPaymentRequest;
}

export function useAddPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, request }: AddPaymentVars) =>
      post<PaymentRow>(
        `${BILLING_BASE}/Payment/Create/Invoice/${invoiceId}`,
        request,
      ),
    onSuccess: (_data, vars) => {
      showSuccess('Payment Added successfully');
      invalidatePaymentAndInvoice(qc, vars.invoiceId);
    },
  });
}

export interface VoidPaymentVars {
  receiptId: string;
  linkedReceiptId?: string;
  invoiceId?: string;
  request: VoidPaymentRequest;
}

export function useVoidPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ receiptId, linkedReceiptId, request }: VoidPaymentVars) => {
      const qs = new URLSearchParams();
      if (linkedReceiptId) qs.set('linkedReceiptId', linkedReceiptId);
      const query = qs.toString();
      const url = `${BILLING_BASE}/Payment/Void/${receiptId}${query ? `?${query}` : ''}`;
      return put<PaymentRow>(url, request);
    },
    onSuccess: (_data, vars) => {
      showSuccess('Amount voided successfully');
      invalidatePaymentAndInvoice(qc, vars.invoiceId);
    },
  });
}

export interface RefundPaymentVars {
  receiptId: string;
  invoiceId?: string;
  /**
   * Max refundable amount on this receipt — passed in by the caller so
   * the hook can validate the React layer never sends an uncapped
   * value (Samer review PR #111). The backend is the ultimate guard,
   * but defense in depth: a direct call from outside the dialog still
   * gets clamped here.
   */
  maxAmount?: number;
  request: RefundPaymentRequest;
}

export function useRefundPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ receiptId, request, maxAmount }: RefundPaymentVars) => {
      // Defensive clamp: refundAmount > 0 and ≤ the receipt's amount.
      // The dialog already enforces this, but a future caller bypassing
      // the dialog must still produce a sane payload.
      const requestedAmount = request.refundAmount ?? 0;
      if (!(requestedAmount > 0)) {
        throw new Error('Refund amount must be greater than 0.');
      }
      const clampedAmount =
        maxAmount != null && requestedAmount > maxAmount
          ? maxAmount
          : requestedAmount;
      return post<PaymentRow>(
        `${BILLING_BASE}/Payment/Refund/${receiptId}`,
        { ...request, refundAmount: clampedAmount },
      );
    },
    onSuccess: (_data, vars) => {
      showSuccess('Refund processed');
      invalidatePaymentAndInvoice(qc, vars.invoiceId);
    },
  });
}
